/**
 * iter229 · AddProductionModal (v2)
 *
 * Two-step AI-powered "+ Add Production" flow:
 *   MODE       →  DRAFT (editable)  →  PREVIEW (read-only confirm)  →  CREATE
 *
 * MODES:
 *   • text   — natural-language box  →  LLM  →  draft
 *   • voice  — record + playback     →  Whisper  →  LLM  →  draft
 *   • image  — upload sheet          →  Vision  →  draft
 *   • manual — start with blank draft
 *
 * DRAFT stage:
 *   Editable form with:
 *     · per-item FG-offset banner + toggle (Plan Ordered ↔ Plan Net)
 *     · per-item batch-size mismatch banner + "Use suggested batches" button
 *     · per-item RM check summary
 *
 * PREVIEW stage:
 *   Read-only confirmation card that mirrors what will be created — one
 *   click "Confirm & Create" runs the bulk-create call.
 *
 * Feasibility policy = WARN ONLY (user choice). RM shortages tag the
 * plan AT RISK but never block save.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Type, Mic, MicOff, Image as ImageIcon, Sparkles, Trash2, Plus,
  AlertTriangle, CheckCircle2, Loader2, Upload, ArrowRight, ArrowLeft,
  Play, RefreshCw, Package, Beaker, Info,
} from 'lucide-react';
import { fmtQty } from '../../utils/format';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PRIORITIES = [
  { v: 'critical', l: 'Critical' },
  { v: 'high',     l: 'High' },
  { v: 'normal',   l: 'Normal' },
  { v: 'low',      l: 'Low' },
];
const PURPOSES = [
  { v: 'customer_order', l: 'Customer Order' },
  { v: 'stock_replenishment', l: 'Stock Replenishment' },
  { v: 'buffer_stock', l: 'Buffer Stock' },
  { v: 'forecast', l: 'Forecast' },
  { v: 'management_decision', l: 'Management Decision' },
  { v: 'other', l: 'Other' },
];

export default function AddProductionModal({ defaultDate, products = [], clients = [], onClose, onCreated }) {
  const [step, setStep] = useState('input');   // 'input' | 'draft' | 'preview'
  const [mode, setMode] = useState('text');    // 'text' | 'voice' | 'image' | 'manual'
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [feasibility, setFeasibility] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  // per-item toggle: "ordered" = plan the full quantity, "net" = plan qty minus existing FG
  const [planTargets, setPlanTargets] = useState({});   // { idx: "ordered" | "net" }

  // Voice recording
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  const [level, setLevel] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const animRef = useRef(null);

  // ─── Voice recording helpers ──────────────────────────────────────
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;
      chunksRef.current = [];
      const type = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm';
      recorderRef.current = new MediaRecorder(stream, { mimeType: type });
      recorderRef.current.ondataavailable = e => e.data.size && chunksRef.current.push(e.data);
      recorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        if (audioCtxRef.current) audioCtxRef.current.close();
        cancelAnimationFrame(animRef.current);
        setLevel(0);
      };

      // Level meter — tiny web-audio analyser
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
        setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 3));
        animRef.current = requestAnimationFrame(tick);
      };
      tick();

      recorderRef.current.start();
      setRecording(true);
    } catch {
      toast.error('Microphone access denied. Please allow mic access in browser settings.');
    }
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setAudioUrl('');
    setTranscript('');
  };

  const parseVoice = async () => {
    if (!audioBlob) return;
    setBusy(true);
    const form = new FormData();
    form.append('audio', audioBlob, 'recording.webm');
    try {
      const res = await axios.post(`${API}/factory-plans/parse/voice`, form, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setTranscript(res.data.transcript || '');
      applyParseResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Voice parsing failed');
    } finally { setBusy(false); }
  };

  // ─── Text / Image parse ───────────────────────────────────────────
  const parseText = async () => {
    if (!text.trim()) { toast.error('Please describe what needs to be produced'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/factory-plans/parse/text`, { text }, { withCredentials: true });
      applyParseResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse text');
    } finally { setBusy(false); }
  };

  const parseImage = async (file) => {
    if (!file) return;
    setBusy(true);
    setImagePreview(URL.createObjectURL(file));
    const form = new FormData();
    form.append('image', file);
    form.append('instruction', text || '');
    try {
      const res = await axios.post(`${API}/factory-plans/parse/image`, form, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      applyParseResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Vision extraction failed');
    } finally { setBusy(false); }
  };

  const applyParseResult = (data) => {
    if (data?.error) { toast.error(data.error); return; }
    if (data?.draft) {
      setDraft(data.draft);
      setFeasibility(data.feasibility || null);
      if (data.transcript) setTranscript(data.transcript);
      setStep('draft');
      const warns = data.draft.warnings || [];
      if (warns.length) toast.info(`AI added ${warns.length} note(s) · review below`);
      else toast.success('Draft ready — review and continue');
    }
  };

  // ─── Feasibility re-check when the user edits the draft ───────────
  const recheckFeasibility = async (newDraft) => {
    try {
      const res = await axios.post(`${API}/factory-plans/feasibility`, newDraft, { withCredentials: true });
      setFeasibility(res.data);
    } catch {/* silent */}
  };

  // ─── Draft editing ────────────────────────────────────────────────
  const updateDraft = (patch) => setDraft(d => ({ ...(d || {}), ...patch }));
  const updateItem = (idx, patch) => {
    setDraft(d => {
      const next = { ...d, items: d.items.map((it, i) => i === idx ? { ...it, ...patch } : it) };
      // Only re-check when qty or batches change (RM math depends on those)
      if ('quantity' in patch || 'batches' in patch || 'product_name' in patch) {
        setTimeout(() => recheckFeasibility(next), 300);
      }
      return next;
    });
  };
  const removeItem = (idx) => setDraft(d => ({ ...d, items: d.items.filter((_, i) => i !== idx) }));
  const addItem = () => setDraft(d => ({
    ...d,
    items: [...(d?.items || []), { product_name: '', quantity: 0, batches: null, unit: 'kg', priority: '' }],
  }));

  // Manual mode → skip AI, start with an empty draft
  useEffect(() => {
    if (mode === 'manual' && !draft) {
      setDraft({
        customer_name: '', sales_order_number: '', planned_date: defaultDate,
        priority: 'normal', purpose: 'stock_replenishment', instructions: '',
        items: [{ product_name: '', quantity: 0, batches: null, unit: 'kg', priority: '' }],
        warnings: [],
      });
      setStep('draft');
    }
  }, [mode, defaultDate, draft]);

  const productSuggestions = useMemo(() => products.filter(p => p.name).slice(0, 500), [products]);

  const feasForItem = (idx, item) => {
    if (!feasibility) return null;
    return (feasibility.items || []).find(fi =>
      (fi.matched_product_id && fi.matched_product_id === item.product_id) ||
      fi.product_name?.toLowerCase() === (item.product_name || '').toLowerCase()
    );
  };

  // Effective quantity per item once the user picks Ordered vs Net.
  const effectiveQtyForItem = (idx, item) => {
    const f = feasForItem(idx, item);
    if (!f || !f.fg_offset || planTargets[idx] !== 'net') return Number(item.quantity) || 0;
    return f.fg_offset.net_production ?? (Number(item.quantity) || 0);
  };

  // ─── Save ─────────────────────────────────────────────────────────
  const save = async () => {
    if (!draft?.items?.length) { toast.error('No items to save'); return; }
    const cleanItems = draft.items
      .map((it, i) => ({ ...it, quantity: effectiveQtyForItem(i, it) }))
      .filter(it => it.product_name && it.quantity > 0);
    if (!cleanItems.length) { toast.error('Every item needs a name and a positive quantity'); return; }

    const matched = clients.find(c => c.name.toLowerCase() === (draft.customer_name || '').toLowerCase());

    setBusy(true);
    try {
      const payload = {
        shared: {
          planned_date: draft.planned_date,
          customer_id: matched?.id || feasibility?.matched_customer?.id || '',
          customer_name: matched?.name || draft.customer_name || '',
          sales_order_number: draft.sales_order_number || '',
          purpose: draft.purpose || 'stock_replenishment',
          priority: draft.priority || 'normal',
          instructions: draft.instructions || '',
        },
        items: cleanItems.map(it => ({
          product_id: it.product_id || '',
          product_name: it.product_name,
          quantity: Number(it.quantity) || 0,
          batches: it.batches ? Number(it.batches) : 0,
          priority: it.priority || draft.priority || 'normal',
        })),
        ai_source: mode,
      };
      const res = await axios.post(`${API}/factory-plans/bulk-create`, payload, { withCredentials: true });
      toast.success(`Created ${res.data.count} production plan(s)`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create plans');
    } finally { setBusy(false); }
  };

  // ─── UI ───────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="fp-ai-add-modal">
      <div className="ti-surface-modal border border-zinc-800 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-auto">
        {/* Header + stepper */}
        <div className="sticky top-0 ti-surface-modal border-b border-zinc-800 px-5 py-3 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-400" />
            <h2 className="font-heading text-lg text-zinc-50">+ AI Add Production</h2>
            <StepPill active={step === 'input'} label="1 · Input" />
            <StepPill active={step === 'draft'} label="2 · Draft" />
            <StepPill active={step === 'preview'} label="3 · Preview" />
          </div>
          <button onClick={onClose} data-testid="fp-ai-close-btn" className="text-zinc-500 hover:text-zinc-200"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* ═══ STEP 1 · INPUT ═══ */}
          {step === 'input' && (
            <>
              <div className="flex items-center gap-1 bg-zinc-800/50 rounded-xl p-1">
                <ModeButton active={mode === 'text'}   onClick={() => setMode('text')}   icon={<Type size={13} />}       label="Text"          testid="fp-mode-text" />
                <ModeButton active={mode === 'voice'}  onClick={() => setMode('voice')}  icon={<Mic size={13} />}        label="Voice"         testid="fp-mode-voice" />
                <ModeButton active={mode === 'image'}  onClick={() => setMode('image')}  icon={<ImageIcon size={13} />}  label="Image / Doc"   testid="fp-mode-image" />
                <ModeButton active={mode === 'manual'} onClick={() => setMode('manual')} icon={<Plus size={13} />}       label="Manual"        testid="fp-mode-manual" />
              </div>

              {mode === 'text' && (
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-400 block">Describe what needs to be produced</label>
                  <textarea value={text} onChange={e => setText(e.target.value)} data-testid="fp-ai-text-input" rows={4}
                    placeholder="e.g. Monday 900 kg Cheese Seasoning, three batches for Anil Traders, high priority. Also 200 kg Chili Powder for stock same day."
                    className="ti-input w-full rounded-lg px-3 py-2 text-sm resize-none" />
                  <button onClick={parseText} disabled={busy || !text.trim()} data-testid="fp-ai-parse-text-btn"
                    className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-semibold text-sm px-4 py-2 rounded-lg">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {busy ? 'Parsing…' : 'Parse with AI'}
                  </button>
                </div>
              )}

              {mode === 'voice' && (
                <VoicePanel
                  recording={recording} level={level} audioBlob={audioBlob} audioUrl={audioUrl} transcript={transcript}
                  busy={busy}
                  onStart={startRecording} onStop={stopRecording} onReset={resetRecording} onParse={parseVoice}
                />
              )}

              {mode === 'image' && (
                <div className="space-y-2">
                  <label className="text-[10px] uppercase tracking-wider text-zinc-400 block">Upload order sheet / PO photo</label>
                  <input type="text" value={text} onChange={e => setText(e.target.value)} data-testid="fp-ai-image-hint-input"
                    placeholder="(optional) Hint: e.g. 'focus on the pending column'"
                    className="ti-input w-full rounded-lg px-3 py-2 text-sm" />
                  <label className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-dashed border-zinc-600 rounded-lg px-4 py-6 text-sm text-zinc-300 cursor-pointer justify-center">
                    <Upload size={14} />
                    <span>{busy ? 'Uploading…' : 'Choose image (JPG / PNG / WEBP)'}</span>
                    <input type="file" accept="image/*" onChange={e => parseImage(e.target.files?.[0])} className="hidden" data-testid="fp-ai-image-input" disabled={busy} />
                  </label>
                  {imagePreview && <img src={imagePreview} alt="preview" className="max-h-48 rounded-lg border border-zinc-800" />}
                </div>
              )}
            </>
          )}

          {/* ═══ STEP 2 · DRAFT ═══ */}
          {step === 'draft' && draft && (
            <div className="space-y-3">
              {transcript && (
                <div className="ti-callout-info">
                  <p className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Voice transcript</p>
                  <p className="text-xs italic">&ldquo;{transcript}&rdquo;</p>
                </div>
              )}

              {draft.warnings?.length > 0 && (
                <ul className="ti-callout-warn text-xs space-y-1">
                  {draft.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                </ul>
              )}

              {/* Shared fields */}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Customer">
                  <input value={draft.customer_name || ''} onChange={e => updateDraft({ customer_name: e.target.value })}
                    list="ai-cust-list" data-testid="fp-ai-customer-input" className="ti-input w-full rounded-lg px-2 py-1.5 text-xs" />
                  <datalist id="ai-cust-list">{clients.map(c => <option key={c.id} value={c.name} />)}</datalist>
                </Field>
                <Field label="Planned Date">
                  <input type="date" value={draft.planned_date || ''} onChange={e => updateDraft({ planned_date: e.target.value })}
                    data-testid="fp-ai-date-input" className="ti-input w-full rounded-lg px-2 py-1.5 text-xs" />
                </Field>
                <Field label="Purpose">
                  <select value={draft.purpose || 'stock_replenishment'} onChange={e => updateDraft({ purpose: e.target.value })}
                    className="ti-input w-full rounded-lg px-2 py-1.5 text-xs">
                    {PURPOSES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </Field>
                <Field label="Priority">
                  <select value={draft.priority || 'normal'} onChange={e => updateDraft({ priority: e.target.value })}
                    className="ti-input w-full rounded-lg px-2 py-1.5 text-xs">
                    {PRIORITIES.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </Field>
              </div>

              {/* Items */}
              <div className="space-y-2">
                {(draft.items || []).map((it, idx) => {
                  const f = feasForItem(idx, it);
                  return (
                    <ItemCard
                      key={idx} idx={idx} item={it} f={f}
                      planTarget={planTargets[idx] || 'ordered'}
                      onPlanTargetChange={t => setPlanTargets(p => ({ ...p, [idx]: t }))}
                      onUpdate={(patch) => updateItem(idx, patch)}
                      onRemove={() => removeItem(idx)}
                      products={productSuggestions}
                    />
                  );
                })}
                <button onClick={addItem} data-testid="fp-ai-add-item-btn"
                  className="w-full text-[11px] py-2 text-amber-400 hover:bg-zinc-800/50 flex items-center justify-center gap-1 border border-dashed border-zinc-700 rounded-lg">
                  <Plus size={11} /> Add another item
                </button>
              </div>

              <Field label="Instructions">
                <textarea value={draft.instructions || ''} onChange={e => updateDraft({ instructions: e.target.value })}
                  rows={2} className="ti-input w-full rounded-lg px-2 py-1.5 text-xs resize-none" />
              </Field>
            </div>
          )}

          {/* ═══ STEP 3 · PREVIEW ═══ */}
          {step === 'preview' && draft && (
            <PreviewCard draft={draft} feasibility={feasibility} planTargets={planTargets}
              effectiveQtyForItem={effectiveQtyForItem} />
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 ti-surface-modal border-t border-zinc-800 px-5 py-3 flex justify-between items-center">
          <div className="text-[11px] text-zinc-500">
            {step === 'draft' && `${(draft?.items || []).length} item(s) queued`}
            {step === 'preview' && `Confirm to create ${(draft?.items || []).filter(i => i.product_name && effectiveQtyForItem(0, i) > 0).length || (draft?.items || []).length} plan(s)`}
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={onClose} className="px-3 py-1.5 text-zinc-400 hover:text-zinc-200 text-sm">Cancel</button>

            {step === 'draft' && (
              <>
                <button onClick={() => setStep('input')} className="px-3 py-1.5 text-zinc-400 hover:text-zinc-200 text-sm flex items-center gap-1"
                  data-testid="fp-ai-back-input-btn">
                  <ArrowLeft size={12} /> Back
                </button>
                <button onClick={() => setStep('preview')} disabled={!draft?.items?.some(i => i.product_name && i.quantity > 0)}
                  data-testid="fp-ai-preview-btn"
                  className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-semibold text-sm rounded-xl flex items-center gap-1.5">
                  Preview <ArrowRight size={13} />
                </button>
              </>
            )}

            {step === 'preview' && (
              <>
                <button onClick={() => setStep('draft')} className="px-3 py-1.5 text-zinc-400 hover:text-zinc-200 text-sm flex items-center gap-1"
                  data-testid="fp-ai-back-draft-btn">
                  <ArrowLeft size={12} /> Back to Edit
                </button>
                <button onClick={save} disabled={busy} data-testid="fp-ai-confirm-btn"
                  className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-700 text-black font-semibold text-sm rounded-xl flex items-center gap-1.5">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                  {busy ? 'Creating…' : 'Confirm & Create'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────

function VoicePanel({ recording, level, audioBlob, audioUrl, transcript, busy, onStart, onStop, onReset, onParse }) {
  return (
    <div className="space-y-3">
      <label className="text-[10px] uppercase tracking-wider text-zinc-400 block">
        Record — auto-detects English / Hindi / Gujarati
      </label>

      {!audioBlob && (
        <div className="flex items-center gap-3">
          {!recording ? (
            <button onClick={onStart} data-testid="fp-ai-voice-start-btn" disabled={busy}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-400 disabled:bg-zinc-700 text-white font-semibold text-sm px-4 py-2 rounded-lg">
              <Mic size={14} /> Start Recording
            </button>
          ) : (
            <>
              <button onClick={onStop} data-testid="fp-ai-voice-stop-btn"
                className="flex items-center gap-2 bg-zinc-600 hover:bg-zinc-500 text-white font-semibold text-sm px-4 py-2 rounded-lg">
                <MicOff size={14} /> Stop
              </button>
              {/* Level meter */}
              <div className="flex-1 h-8 rounded-lg overflow-hidden bg-zinc-800 flex items-center px-2 gap-0.5">
                {Array.from({ length: 24 }).map((_, i) => {
                  const active = i / 24 <= level;
                  return <div key={i}
                    className={`flex-1 rounded-sm transition-all ${active
                      ? (i > 18 ? 'bg-red-400' : i > 12 ? 'bg-amber-400' : 'bg-emerald-400')
                      : 'bg-zinc-700'}`}
                    style={{ height: active ? `${8 + (i % 6) * 3}px` : '4px' }} />;
                })}
              </div>
              <span className="text-[10px] text-red-300 animate-pulse font-semibold">● REC</span>
            </>
          )}
        </div>
      )}

      {audioBlob && (
        <div className="space-y-2">
          <div className="ti-surface-inset rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Play size={13} className="text-emerald-400" />
              <span className="text-xs text-zinc-300">Preview your recording</span>
              <div className="flex-1" />
              <button onClick={onReset} data-testid="fp-ai-voice-reset-btn"
                className="text-xs text-zinc-400 hover:text-zinc-100 flex items-center gap-1">
                <RefreshCw size={11} /> Re-record
              </button>
            </div>
            <audio src={audioUrl} controls className="w-full h-8" />
          </div>
          <button onClick={onParse} disabled={busy} data-testid="fp-ai-voice-parse-btn"
            className="w-full flex items-center justify-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:bg-zinc-700 text-black font-semibold text-sm px-4 py-2 rounded-lg">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {busy ? 'Understanding…' : 'Understand with AI'}
          </button>
        </div>
      )}

      {transcript && (
        <div className="ti-callout-info">
          <p className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Transcript</p>
          <p className="text-xs italic">&ldquo;{transcript}&rdquo;</p>
        </div>
      )}
    </div>
  );
}

function ItemCard({ idx, item, f, planTarget, onPlanTargetChange, onUpdate, onRemove, products }) {
  const batchIssue = f?.batch_check;
  const hasBatchWarning = batchIssue && (batchIssue.match === 'over' || batchIssue.match === 'under');
  const fg = f?.fg_offset;
  const hasFgOffset = fg && (fg.offset > 0.001);
  const rmShort = (f?.rm_short || []).length > 0;

  return (
    <div className="ti-surface-inset border border-zinc-700 rounded-lg p-3 space-y-2">
      {/* Row 1 · Product + qty */}
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-6">
          <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">Product</label>
          <input value={item.product_name || ''} onChange={e => onUpdate({ product_name: e.target.value, product_id: '' })}
            list={`ai-prod-list-${idx}`} data-testid={`fp-ai-item-name-${idx}`}
            className="ti-input w-full rounded px-2 py-1 text-sm" />
          <datalist id={`ai-prod-list-${idx}`}>
            {products.map(p => <option key={p.id} value={p.name} />)}
          </datalist>
        </div>
        <div className="col-span-2">
          <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">Qty</label>
          <input type="number" step="any" value={item.quantity || ''} onChange={e => onUpdate({ quantity: Number(e.target.value) })}
            data-testid={`fp-ai-item-qty-${idx}`}
            className="ti-input w-full rounded px-2 py-1 text-sm text-right font-mono" />
        </div>
        <div className="col-span-1">
          <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">Unit</label>
          <input value={item.unit || 'kg'} onChange={e => onUpdate({ unit: e.target.value })}
            className="ti-input w-full rounded px-2 py-1 text-sm text-center" />
        </div>
        <div className="col-span-2">
          <label className="text-[9px] uppercase tracking-wider text-zinc-500 block">Batches</label>
          <input type="number" value={item.batches || ''} onChange={e => onUpdate({ batches: e.target.value ? Number(e.target.value) : null })}
            data-testid={`fp-ai-item-batches-${idx}`}
            className="ti-input w-full rounded px-2 py-1 text-sm text-right font-mono" />
        </div>
        <div className="col-span-1 flex items-end justify-end">
          <button onClick={onRemove} className="text-zinc-500 hover:text-red-400 p-1" data-testid={`fp-ai-item-remove-${idx}`}>
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* AI intelligence strips */}
      <div className="space-y-1.5">
        {/* Batch mismatch */}
        {hasBatchWarning && (
          <div className="ti-callout-warn flex items-start gap-2">
            <Beaker size={13} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold">Batch size mismatch</p>
              <p className="text-[11px] opacity-90 mt-0.5">
                Recipe standard: <b>{fmtQty(batchIssue.recipe_batch_kg)} kg/batch</b>.
                You&apos;ve asked for <b>{item.batches} batches</b> × <b>{fmtQty(batchIssue.requested_per_batch_kg)} kg/batch</b>.
              </p>
              <p className="text-[11px] opacity-90 mt-0.5">
                Suggested: <b>{batchIssue.suggested_batches} batches</b> → {fmtQty(batchIssue.suggested_total_kg)} kg total ·
                per-batch = {fmtQty(batchIssue.recipe_batch_kg)} kg.
              </p>
              <div className="flex gap-2 mt-1.5">
                <button onClick={() => onUpdate({ batches: batchIssue.suggested_batches, quantity: batchIssue.suggested_total_kg })}
                  data-testid={`fp-ai-use-suggested-batches-${idx}`}
                  className="text-[11px] px-2 py-1 rounded bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                  Use {batchIssue.suggested_batches} batches
                </button>
                <button onClick={() => onUpdate({ batches: batchIssue.suggested_batches })}
                  className="text-[11px] px-2 py-1 rounded ti-btn-ghost">
                  Keep {item.quantity} kg, fix batches only
                </button>
              </div>
            </div>
          </div>
        )}

        {/* FG offset */}
        {hasFgOffset && (
          <div className="ti-callout-info flex items-start gap-2">
            <Package size={13} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold">Finished goods already available</p>
              <p className="text-[11px] opacity-90 mt-0.5">
                Ordered: <b>{fmtQty(item.quantity)} {item.unit}</b> ·
                Existing FG: <b>{fmtQty(fg.on_hand)} {fg.fg_unit}</b> ·
                Net to Produce: <b>{fmtQty(fg.net_production)} {item.unit}</b>.
              </p>
              <div className="flex gap-2 mt-1.5">
                <button onClick={() => onPlanTargetChange('ordered')}
                  data-testid={`fp-ai-plan-ordered-${idx}`}
                  className={`text-[11px] px-2 py-1 rounded font-semibold ${planTarget === 'ordered' ? 'bg-amber-500 text-black' : 'ti-btn-ghost'}`}>
                  Plan Ordered ({fmtQty(item.quantity)})
                </button>
                <button onClick={() => onPlanTargetChange('net')}
                  data-testid={`fp-ai-plan-net-${idx}`}
                  className={`text-[11px] px-2 py-1 rounded font-semibold ${planTarget === 'net' ? 'bg-emerald-500 text-black' : 'ti-btn-ghost'}`}>
                  Plan Net ({fmtQty(fg.net_production)})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* RM shortages */}
        {rmShort && (
          <div className="ti-callout-error flex items-start gap-2">
            <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold">Raw material shortage — plan will be ⚠ AT RISK</p>
              <ul className="text-[11px] opacity-90 mt-0.5 space-y-0.5">
                {f.rm_short.slice(0, 4).map((s, i) => (
                  <li key={i}>• {s.material_name}: need {fmtQty(s.required, s.unit)}, on hand {fmtQty(s.on_hand, s.unit)}, <b>short by {fmtQty(s.short_by, s.unit)}</b></li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* All good */}
        {f && !hasBatchWarning && !rmShort && (f.rm_check || []).length > 0 && (
          <div className="ti-callout-success flex items-center gap-2">
            <CheckCircle2 size={13} />
            <p className="text-xs">All raw materials available · batch size matches recipe.</p>
          </div>
        )}

        {/* No recipe */}
        {f?.warning && (
          <div className="ti-callout-neutral flex items-start gap-2">
            <Info size={12} className="flex-shrink-0 mt-0.5" />
            <p className="text-[11px] opacity-90">{f.warning}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewCard({ draft, feasibility, planTargets, effectiveQtyForItem }) {
  const anyRmShort = (feasibility?.shortages || []).length > 0;
  const totalPlans = (draft.items || []).length;
  const totalQty = (draft.items || []).reduce((s, it, i) => s + effectiveQtyForItem(i, it), 0);
  const totalBatches = (draft.items || []).reduce((s, it, i) => {
    const f = (feasibility?.items || []).find(fi =>
      (fi.matched_product_id && fi.matched_product_id === it.product_id) ||
      fi.product_name?.toLowerCase() === (it.product_name || '').toLowerCase()
    );
    const bc = f?.batch_check;
    return s + (Number(it.batches) || bc?.suggested_batches || 0);
  }, 0);

  return (
    <div className="space-y-3" data-testid="fp-ai-preview-panel">
      <div className="ti-callout-neutral text-xs">
        Review below · Click <b>Confirm &amp; Create</b> to lock in the plan. Nothing is created until you confirm.
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-2">
        <KpiCell l="Plans" v={totalPlans} />
        <KpiCell l="Total Qty" v={fmtQty(totalQty)} />
        <KpiCell l="Total Batches" v={totalBatches || '—'} />
        <KpiCell l="Priority" v={(draft.priority || 'normal').toUpperCase()} tone={draft.priority} />
      </div>

      {/* Header block */}
      <div className="ti-surface-inset rounded-lg p-3 space-y-1">
        <RowKV k="Customer"    v={draft.customer_name || '— (buffer stock)'} />
        <RowKV k="Planned Date" v={draft.planned_date} />
        <RowKV k="Purpose"     v={(PURPOSES.find(p => p.v === draft.purpose)?.l) || draft.purpose} />
        {draft.sales_order_number && <RowKV k="SO Number" v={draft.sales_order_number} />}
        {draft.instructions && <RowKV k="Instructions" v={draft.instructions} />}
      </div>

      {/* Line items */}
      {(draft.items || []).map((it, i) => {
        const f = (feasibility?.items || []).find(fi =>
          (fi.matched_product_id && fi.matched_product_id === it.product_id) ||
          fi.product_name?.toLowerCase() === (it.product_name || '').toLowerCase()
        );
        const eff = effectiveQtyForItem(i, it);
        const target = planTargets[i] || 'ordered';
        return (
          <div key={i} className="ti-surface-inset rounded-lg p-3 space-y-1">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm text-zinc-50">{it.product_name}</p>
              <span className="text-xs text-zinc-400 font-mono">
                {fmtQty(eff)} {it.unit || 'kg'}
                {target === 'net' && <span className="text-emerald-400 ml-1">· net</span>}
                {' '}· {it.batches || f?.batch_check?.suggested_batches || '?'} batch(es)
              </span>
            </div>
            {f?.fg_offset?.offset > 0.001 && target === 'net' && (
              <p className="text-[11px] text-blue-300">↳ Skipping {fmtQty(f.fg_offset.offset)} {it.unit} already in FG</p>
            )}
            {f?.rm_short?.length > 0 && (
              <p className="text-[11px] text-red-300">⚠ Will be tagged AT RISK — {f.rm_short.length} raw material(s) short</p>
            )}
          </div>
        );
      })}

      {anyRmShort && (
        <div className="ti-callout-warn text-[11px]">
          <b>Note:</b> {(feasibility.shortages || []).length} raw material shortage(s) detected. Plans will be flagged <b>AT RISK</b> per your policy — save is not blocked.
        </div>
      )}
    </div>
  );
}

function StepPill({ active, label }) {
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ml-2 ${active ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-zinc-800 text-zinc-500 border border-zinc-700'}`}>
      {label}
    </span>
  );
}
function ModeButton({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${active ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'}`}>
      {icon} {label}
    </button>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}
function KpiCell({ l, v, tone }) {
  return (
    <div className="ti-surface-inset border border-zinc-700 rounded-lg p-2">
      <p className="text-[9px] uppercase tracking-wider text-zinc-500">{l}</p>
      <p className={`text-sm font-heading font-bold text-zinc-50 ${tone === 'critical' ? 'text-red-300' : tone === 'high' ? 'text-orange-300' : ''}`}>{v}</p>
    </div>
  );
}
function RowKV({ k, v }) {
  return (
    <div className="flex justify-between items-start gap-2 text-xs">
      <span className="text-zinc-500 uppercase text-[10px] tracking-wider">{k}</span>
      <span className="text-zinc-200 text-right">{v}</span>
    </div>
  );
}
